import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useAppSelector } from "@common-reducers/hooks/store.hook";
import { MessageCard } from "./MessageCard";
import { StatusCell } from "./StatusCell";
import { ContactCell } from "./ContactCell";
import { CampaignIdCell } from "./CampaignIdCell";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronRight, Repeat, Calendar } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useState, Fragment, useCallback, useMemo, useEffect, useRef } from "react";
import moment from "moment";
import { UserMessage } from "@bt-common/schemas/bt/user-message";
import { selectRecurrenceInfoFromMessage } from "@common-reducers/CampaignSelectors";

interface MessageTableProps {
    messages: UserMessage[];
    onMediaClick?: (message: UserMessage) => void;
}

const INITIAL_ROWS_TO_RENDER = 50;
const ROWS_PER_LOAD = 50;

export function MessageTable({ messages, onMediaClick }: MessageTableProps) {
    const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
    const [visibleRowsCount, setVisibleRowsCount] = useState(INITIAL_ROWS_TO_RENDER);
    const loadMoreRef = useRef<HTMLDivElement>(null);

    const toggleRow = useCallback((id: string) => {
        setExpandedRows(prev => {
            const isExpanded = prev[id];
            return {
                ...prev,
                [id]: !isExpanded
            };
        });
    }, []);

    // Reset visible rows when messages change
    useEffect(() => {
        setVisibleRowsCount(INITIAL_ROWS_TO_RENDER);
    }, [messages.length]);

    // Infinite scroll with Intersection Observer
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                const firstEntry = entries[0];
                if (firstEntry?.isIntersecting) {
                    setVisibleRowsCount(prev => {
                        const newCount = Math.min(prev + ROWS_PER_LOAD, messages.length);
                        return newCount;
                    });
                }
            },
            {
                root: null,
                rootMargin: '100px',
                threshold: 0.1
            }
        );

        const currentRef = loadMoreRef.current;
        if (currentRef) {
            observer.observe(currentRef);
        }

        return () => {
            if (currentRef) {
                observer.unobserve(currentRef);
            }
        };
    }, [messages.length, visibleRowsCount]);

    const visibleMessages = useMemo(() => {
        return messages.slice(0, visibleRowsCount);
    }, [messages, visibleRowsCount]);

    const hasMore = visibleRowsCount < messages.length;

    if (messages.length === 0) {
        return (
            <div className="text-center py-8 text-slate-500">
                No messages found
            </div>
        );
    }

    return (
        <div className="rounded-md border">
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead className="w-[50px]"></TableHead>
                        <TableHead>Message</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Due Date</TableHead>
                        <TableHead>Next Due Date</TableHead>
                        <TableHead>Contact</TableHead>
                        <TableHead>Campaign ID</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {visibleMessages.map((message, index) => (
                        <MessageTableRow 
                            key={message._id || index}
                            message={message}
                            isExpanded={expandedRows[message._id || ''] || false}
                            onToggle={() => toggleRow(message._id || '')}
                            onMediaClick={onMediaClick}
                            index={index}
                        />
                    ))}
                </TableBody>
            </Table>
            {hasMore && (
                <div 
                    ref={loadMoreRef}
                    className="flex justify-center py-4 border-t"
                >
                    <div className="text-sm text-slate-500">
                        Loading more... ({messages.length - visibleRowsCount} remaining)
                    </div>
                </div>
            )}
        </div>
    );
}

function MessageTableRow({ 
    message, 
    isExpanded, 
    onToggle, 
    onMediaClick,
    index
}: { 
    message: UserMessage; 
    isExpanded: boolean; 
    onToggle: () => void;
    onMediaClick?: (message: UserMessage) => void;
    index: number;
}) {
    const isEvenRow = index % 2 === 0;
    const rowBgClass = isEvenRow ? 'bg-slate-50' : 'bg-white';
    const messageText = message.message || '';
    const contact = message.contactList?.[0];

    // Get recurrence info to show next due date
    const recurrenceInfo = useAppSelector(state => 
        selectRecurrenceInfoFromMessage(state, message)
    );

    return (
        <Fragment>
            <TableRow 
                className={`cursor-pointer ${rowBgClass}`}
                onClick={onToggle}
            >
                <TableCell>
                    <Collapsible open={isExpanded} onOpenChange={onToggle}>
                        <CollapsibleTrigger asChild>
                            <div 
                                className="cursor-pointer"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <ChevronRight
                                    className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                                />
                            </div>
                        </CollapsibleTrigger>
                    </Collapsible>
                </TableCell>
                <TableCell>
                    <div className="max-w-[300px]">
                        <div className="flex items-center gap-2">
                            <div className="text-sm font-medium truncate flex-1">
                                {messageText.substring(0, 60) || 'No content'}{messageText.length > 60 ? '...' : ''}
                            </div>
                            {message.isRecurring && (
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <div className="cursor-help">
                                            <Repeat className="h-4 w-4 text-purple-600 dark:text-purple-400 flex-shrink-0" />
                                        </div>
                                    </TooltipTrigger>
                                    <TooltipContent side="top">
                                        <p className="text-xs">Recurring message</p>
                                    </TooltipContent>
                                </Tooltip>
                            )}
                        </div>
                        {contact && (
                            <div className="text-xs text-slate-500 truncate">
                                {contact.name || contact.id}
                            </div>
                        )}
                    </div>
                </TableCell>
                <TableCell>
                    <StatusCell status={message.status} dueDate={message.dueDate} />
                </TableCell>
                <TableCell>
                    <span className="text-sm text-slate-600">
                        {message.dueDate ? moment(message.dueDate).format('DD/MM/YYYY, h:mm:ss A') : '-'}
                    </span>
                </TableCell>
                <TableCell>
                    {recurrenceInfo?.nextDueDate ? (
                        <div className="flex items-center gap-1.5">
                            <Calendar className="h-3 w-3 text-purple-600 dark:text-purple-400" />
                            <span className="text-sm text-purple-700 dark:text-purple-400">
                                {recurrenceInfo.nextDueDate.format('DD/MM/YYYY, h:mm:ss A')}
                            </span>
                        </div>
                    ) : (
                        <span className="text-sm text-slate-400">-</span>
                    )}
                </TableCell>
                <TableCell>
                    <ContactCell contact={contact} />
                </TableCell>
                <TableCell>
                    <CampaignIdCell campaignId={message.campaignId} />
                </TableCell>
            </TableRow>
            {isExpanded && (
                <TableRow className={rowBgClass}>
                    <TableCell colSpan={7} className="p-0">
                        <div className={`p-4 ${isEvenRow ? 'bg-slate-50' : 'bg-white'}`}>
                            <MessageCard message={message} onMediaClick={onMediaClick} />
                        </div>
                    </TableCell>
                </TableRow>
            )}
        </Fragment>
    );
}

